/**
 * Task 9 — Known Error Suppression: duration rules.
 *
 * All suppression date arithmetic and formatting lives here, and nowhere else. The rules are
 * fully specified by the BDD spec (presets of 7/14/30/90/180 days, a custom date, or
 * "Indefinitely", with custom dates validated to 7-180 days inclusive), so this module is the
 * one place a duration decision is made.
 *
 * Deliberately dependency-free — no moment, no date-fns. The rest of the SPA formats dates with
 * moment, but this module is pure logic with exact boundary conditions the spec enumerates
 * (3 rejected, 7 accepted, 180 accepted, 200 rejected), and keeping it free of imports means it
 * can be exercised directly in isolation rather than only through the UI.
 *
 * Timezone rule (client question C2, CONFIRMED 2026-08-17): the customer picks a calendar date in
 * their own local time and the suppression stays active until the END of that day locally; the
 * resolved instant is stored and compared in UTC.
 *
 * The 7 and 180 day bounds are inclusive (C3, confirmed 2026-08-17).
 */

export const PRESET_DAYS = [7, 14, 30, 90, 180];
export const MIN_DAYS = 7;
export const MAX_DAYS = 180;

/**
 * Maximum length of the optional justification note (C5, confirmed 2026-08-17: "optional
 * justification, limit it to 80 chars, ensure the field is validated and escaped").
 *
 * The length rule lives here with every other suppression rule rather than inline in the form, so
 * the field's maxLength and the value actually accepted cannot drift apart.
 */
export const NOTE_MAX_LENGTH = 80;

/**
 * D4 — how many days an ended suppression stays visible and re-enableable (confirmed 2026-08-17).
 *
 * Lives here rather than with the fixtures because the SCREENS quote this number to the customer —
 * and the fixtures module has since been deleted, which is exactly the breakage this move avoided.
 * The server holds the same number in JobErrorSuppressionService.EndedVisibilityDays; if one changes,
 * both must, or the window the customer is shown will not match the window that is enforced.
 */
export const ENDED_VISIBILITY_DAYS = 14;

export const DURATION_MODE = {
    PRESET: "preset",
    CUSTOM: "custom",
    INDEFINITE: "indefinite",
};

/** Local midnight at the start of the given date (or today). */
function startOfLocalDay(value) {
    const d = value ? new Date(value) : new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

/** Local end-of-day (23:59:59.999) for the given date. */
function endOfLocalDay(value) {
    const d = value ? new Date(value) : new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

/**
 * Whole calendar days between today and the given date, in the customer's local time.
 *
 * Counted on calendar dates rather than elapsed milliseconds: the spec talks in
 * "<days_from_now> days from now", and a customer picking a date from a calendar is choosing a
 * day, not a 24-hour multiple. Building both endpoints at local midnight and rounding the
 * difference also makes this immune to daylight-saving transitions, where a naive
 * milliseconds/86400000 would land on 6.958 or 7.042 days and floor to the wrong answer.
 */
export function daysFromToday(date) {
    if (!date) return null;
    const target = startOfLocalDay(date);
    if (isNaN(target.getTime())) return null;
    const today = startOfLocalDay();
    return Math.round((target.getTime() - today.getTime()) / 86400000);
}

/**
 * Validates a custom date against the agreed 7-180 day window (inclusive at both ends).
 * Returns { valid, days, message }.
 */
export function validateCustomDate(date) {
    if (!date) {
        return { valid: false, days: null, message: "Please choose a date." };
    }
    const days = daysFromToday(date);
    if (days === null) {
        return { valid: false, days: null, message: "Please choose a valid date." };
    }
    if (days < MIN_DAYS) {
        return {
            valid: false,
            days,
            message: `The earliest date you can choose is ${MIN_DAYS} days from today.`,
        };
    }
    if (days > MAX_DAYS) {
        return {
            valid: false,
            days,
            message: `The latest date you can choose is ${MAX_DAYS} days from today. Choose "Indefinitely" if this error should stay suppressed with no expiry.`,
        };
    }
    return { valid: true, days, message: null };
}

/**
 * Validates the optional justification note. Returns { valid, length, message }.
 *
 * Absent, empty and whitespace-only are all VALID — the note is optional, so "no reason given" is a
 * legitimate choice and must not block the customer.
 *
 * An input's maxLength attribute is a convenience, not a guarantee: paste and autofill can exceed it
 * in some browsers, so this is the authority and the Finish button gates on it.
 */
export function validateNote(note) {
    const value = (note || "").trim();
    if (value.length === 0) {
        return { valid: true, length: 0, message: null };
    }
    if (value.length > NOTE_MAX_LENGTH) {
        return {
            valid: false,
            length: value.length,
            message: `Keep the reason to ${NOTE_MAX_LENGTH} characters or fewer (currently ${value.length}).`,
        };
    }
    return { valid: true, length: value.length, message: null };
}

/** Earliest date the picker should allow (today + MIN_DAYS). */
export function minSelectableDate() {
    const d = startOfLocalDay();
    d.setDate(d.getDate() + MIN_DAYS);
    return d;
}

/** Latest date the picker should allow (today + MAX_DAYS). */
export function maxSelectableDate() {
    const d = startOfLocalDay();
    d.setDate(d.getDate() + MAX_DAYS);
    return d;
}

/**
 * Turns a duration choice into the value sent to the API.
 * Returns a UTC ISO-8601 string, or null for an indefinite suppression.
 *
 * A preset of N days and a custom date N days out resolve to the SAME instant, so the two routes
 * to the same duration cannot disagree.
 */
export function resolveExpiry({ mode, presetDays, customDate }) {
    if (mode === DURATION_MODE.INDEFINITE) return null;

    // N3 SETTLED 2026-08-17: "It should run to the end of the day. Whether presets or users, they
    // should run to 23:59 on the day selected." So a preset resolves to the END of the final day,
    // exactly as a custom date does — which is why every expiry the customer is shown is a plain date
    // rather than a timestamp. Do not change this to the exact time of day without also changing
    // describeExpiry() and formatExpiry(), or the grid would display a date that disagrees with the
    // stored instant.
    if (mode === DURATION_MODE.PRESET) {
        if (!PRESET_DAYS.includes(presetDays)) return null;
        const d = startOfLocalDay();
        d.setDate(d.getDate() + presetDays);
        return endOfLocalDay(d).toISOString();
    }

    if (mode === DURATION_MODE.CUSTOM) {
        const check = validateCustomDate(customDate);
        if (!check.valid) return null;
        return endOfLocalDay(customDate).toISOString();
    }

    return null;
}

/** True when the choice is complete and valid enough to submit. */
export function isDurationValid({ mode, presetDays, customDate }) {
    if (mode === DURATION_MODE.INDEFINITE) return true;
    if (mode === DURATION_MODE.PRESET) return PRESET_DAYS.includes(presetDays);
    if (mode === DURATION_MODE.CUSTOM) return validateCustomDate(customDate).valid;
    return false;
}

/** Display a stored expiry: "Indefinite" when null, else DD-MM-YYYY. */
export function formatExpiry(value) {
    if (value === null || value === undefined || value === "") return "Indefinite";
    return formatDate(value);
}

/** Display any date as DD-MM-YYYY, matching the grid convention used elsewhere in the SPA. */
export function formatDate(value) {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

/** Display a stored timestamp as DD-MM-YYYY HH:mm. */
export function formatDateTime(value) {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${formatDate(value)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Plain-English confirmation of what the customer has chosen, shown back to them before they
 * commit. Deliberately restates the resolved date rather than the duration they picked, so a
 * timezone or boundary surprise is visible before it is saved rather than after.
 */
export function describeExpiry({ mode, presetDays, customDate }) {
    if (mode === DURATION_MODE.INDEFINITE) {
        return "This error will stay suppressed until you remove the suppression.";
    }
    const iso = resolveExpiry({ mode, presetDays, customDate });
    if (!iso) return "";
    const days = mode === DURATION_MODE.PRESET ? presetDays : daysFromToday(customDate);
    return `Suppressed until the end of ${formatDate(iso)} (${days} days from today).`;
}
